import { useState, useRef, useEffect, useCallback, useLayoutEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { arrayMove, useSortable } from "@dnd-kit/sortable";
import { Upload, FolderOpen, X, GripVertical, Loader2, ImageIcon } from "lucide-react";
import type { AdminDestinationListItem } from "@ckgo2/shared";
import type { MediaFolder } from "@ckgo2/shared/types";
import { useMedia } from "@/hooks/useMedia";
import { useAdminDestinationList } from "@/hooks/useAdminLists";
import { MEDIA_LIST_KEY, useMediaSources } from "@/hooks/useMediaSources";
import {
  activeMediaFilterCount,
  clearMediaFilters,
  mediaGridState,
  setMediaFilter,
  useMediaLibrary,
  type MediaFilterKey,
  type MediaLibrary,
  type MediaLibraryFilters,
} from "@/hooks/useMediaLibrary";
import { DndCSS, type DndTransform } from "@/lib/dndHelpers";
import DraggableList from "@/components/common/DraggableList";
import GalleryPrimaryAction from "@/components/common/GalleryPrimaryAction";
import { useMediaPickerScope } from "@/components/common/MediaPickerScope";
import { setGalleryPrimary } from "@/components/common/galleryPrimary";
import AlbumTree from "@/components/media/AlbumTree";
import MediaFilters, { MediaFolderFilter } from "@/components/media/MediaFilters";
import PhotoViewer from "@/components/media/PhotoViewer";
import BatchUploadPanel from "@/components/media/upload/BatchUploadPanel";
import type { BatchUpload } from "@/components/media/upload/batchUpload.types";
import { useHostedBatch } from "@/components/media/useHostedBatch";
import {
  pickerAdditions,
  singlePickerChoice,
  uploadCountriesFromFilter,
} from "@/components/media/batchUploadHost";
import PickerGrid from "@/components/media/picker/PickerGrid";
import PickerFooter from "@/components/media/picker/PickerFooter";
import {
  addPicked,
  galleryAfterAdd,
  pickerFilters,
  pickerInitialParams,
  pickerParamsForScope,
  pickerTileState,
  shouldCloseOnEscape,
  togglePicked,
  type PickerContext,
  type PickerTileState,
} from "@/components/media/picker/pickerModel";

/**
 * Stabilní prázdný seznam pro seznam destinací, který se nenačetl ani jednou (nové `[]` každým
 * renderem by měnilo země panelu dávky; precedent `MediaUploadArea`).
 */
const NO_ROWS: readonly AdminDestinationListItem[] = [];

/** Společné props obou režimů (řez 05, Kontrakt bod 3); tělo je destrukturuje nad sjednocením. */
interface CommonProps {
  folder?: MediaFolder;
  label?: string;
  className?: string;
  /**
   * Hero místo (pole ze seznamu `heroPole` v `scripts/kontrola/vyber-fotek.json`, řez 09,
   * F8.6): mřížka okna označí fotky na výšku štítkem „na výšku, ořízne se“.
   */
  purpose?: "hero";
}

interface SingleProps extends CommonProps {
  mode?: "single";
  value: string | null | undefined;
  onChange: (id: string | null) => void;
  /** Render as a controlled modal only (no inline thumb/trigger). Used by the Tiptap editor. */
  hideTrigger?: boolean;
  /** Controlled open state (only meaningful with hideTrigger). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface MultipleProps extends CommonProps {
  mode: "multiple" | "gallery";
  value: string[] | undefined;
  onChange: (ids: string[]) => void;
  /** Galerijní místo: hlavní fotka téhož formuláře (F7.12). */
  primary?: { value: string | null; onChange: (id: string | null) => void };
}

type Props = SingleProps | MultipleProps;

function Thumb({ id, onRemove }: { id: string; onRemove?: () => void }) {
  const { t } = useTranslation();
  const { data } = useMedia(id);
  const media = data?.data;
  return (
    <div
      data-media-picker-thumb=""
      data-media-id={id}
      className="group relative aspect-square overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800"
    >
      {media ? (
        <img src={media.thumbUrl} alt={media.alt} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="bg-error-500/90 hover:bg-error-500 absolute right-1 top-1 rounded p-1 text-white opacity-0 transition group-hover:opacity-100"
          aria-label={t("common.actions.remove")}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/**
 * Drag-reorderable thumbnail for multiple/gallery mode. Order is meaningful —
 * the first item is the gallery cover — so editors can sort via the drag handle
 * (pointer + keyboard, both supplied by DraggableList's sensors). Index badge
 * makes the cover/position explicit.
 */
function SortableThumb({
  id,
  index,
  onRemove,
  onSetPrimary,
}: {
  id: string;
  index: number;
  onRemove: () => void;
  /** Galerijní místo s hlavní fotkou (F7.12): akce „Nastavit jako hlavní“ v pravém dolním rohu. */
  onSetPrimary?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const { data } = useMedia(id);
  const media = data?.data;

  const style = {
    transform: DndCSS.Transform.toString(transform as DndTransform | null),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-media-picker-thumb=""
      data-media-id={id}
      className={`group relative aspect-square overflow-hidden rounded-lg border-2 bg-gray-100 dark:bg-gray-800 ${
        isDragging ? "border-primary-500 shadow-lg" : "border-transparent"
      }`}
    >
      {media ? (
        <img src={media.thumbUrl} alt={media.alt} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}

      <button
        type="button"
        className="absolute left-1 top-1 cursor-grab touch-none rounded bg-white/90 p-1 text-gray-600 opacity-0 shadow transition-opacity group-hover:opacity-100 dark:bg-gray-800/90 dark:text-gray-300"
        aria-label={t("shared.mediaPicker.dragToReorder")}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <button
        type="button"
        onClick={onRemove}
        className="bg-error-500/90 hover:bg-error-500 absolute right-1 top-1 rounded p-1 text-white opacity-0 transition group-hover:opacity-100"
        aria-label={t("common.actions.remove")}
      >
        <X className="h-3.5 w-3.5" />
      </button>

      <div className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-xs font-medium text-white">
        {index + 1}
      </div>

      {onSetPrimary && <GalleryPrimaryAction mediaId={id} onSetPrimary={onSetPrimary} />}
    </div>
  );
}

/**
 * Zápisy do pole editoru podle režimu (sjednocení props rozdělené jednou): jednoduchý výběr
 * vybírá a maže hodnotu, vícenásobný přidává „Přidat vybrané“ za dosavadní (`galleryAfterAdd`),
 * odebírá, řadí a s `primary` nastavuje hlavní fotku (F7.12, `setGalleryPrimary`).
 */
function pickerField(props: Props): {
  selectedIds: string[];
  select: (id: string) => void;
  add: (ids: string[]) => void;
  remove: (id: string) => void;
  reorder: (oldIndex: number, newIndex: number) => void;
  setPrimary: ((id: string) => void) | undefined;
} {
  const noop = () => {};
  if (props.mode === "multiple" || props.mode === "gallery") {
    const value = props.value ?? [];
    const { onChange, primary } = props;
    return {
      selectedIds: value,
      select: noop,
      add: (ids) => onChange(galleryAfterAdd(value, ids)),
      remove: (id) => onChange(value.filter((x) => x !== id)),
      reorder: (oldIndex, newIndex) => onChange(arrayMove(value, oldIndex, newIndex)),
      setPrimary: primary
        ? (id) => {
            const r = setGalleryPrimary(value, primary.value, id);
            onChange(r.gallery);
            primary.onChange(r.primary);
          }
        : undefined,
    };
  }
  // `mode?: "single"` je nepovinný, takže TS zbytek sjednocení sám nezúží.
  const { value, onChange } = props as SingleProps;
  return {
    selectedIds: value ? [value] : [],
    select: (id) => onChange(id),
    add: noop,
    remove: () => onChange(null),
    reorder: noop,
    setPrimary: undefined,
  };
}

export default function MediaPicker(props: Props) {
  const { t } = useTranslation();
  const { folder = "general", label, className = "", purpose } = props;
  const mode = props.mode ?? "single";
  const isMultiple = mode !== "single";
  const single = !isMultiple ? (props as SingleProps) : null;
  const hideTrigger = single?.hideTrigger ?? false;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = single?.open ?? internalOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      single?.onOpenChange?.(next);
      setInternalOpen(next);
    },
    [single],
  );

  const field = pickerField(props);
  const { selectedIds } = field;

  // Jednoduchý výběr: klik na fotku (nebo konec dávky) ji vybere a okno zavře. Vícenásobný
  // výběr mění pole až „Přidat vybrané (N)“ (`field.add`, jednou za zavření okna).
  const handleSelect = (id: string) => {
    field.select(id);
    setOpen(false);
  };

  const modal = (
    <MediaModal
      folder={folder}
      selectedIds={selectedIds}
      mode={mode}
      onSelect={handleSelect}
      onAdd={field.add}
      onClose={() => setOpen(false)}
      purpose={purpose}
    />
  );

  // Controlled modal-only mode (Tiptap editor): render just the modal.
  if (hideTrigger) return open ? modal : null;

  return (
    <div className={className}>
      {label && (
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}

      {selectedIds.length > 0 ? (
        isMultiple ? (
          <DraggableList itemIds={selectedIds} strategy="grid" onReorder={field.reorder}>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {selectedIds.map((id, index) => (
                <SortableThumb
                  key={id}
                  id={id}
                  index={index}
                  onRemove={() => field.remove(id)}
                  onSetPrimary={field.setPrimary}
                />
              ))}
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="flex aspect-square items-center justify-center rounded-lg border-2 border-dashed border-gray-300 text-gray-400 hover:border-gray-400 dark:border-gray-600"
              >
                <Upload className="h-6 w-6" />
              </button>
            </div>
          </DraggableList>
        ) : (
          <div className="max-w-xs">
            {selectedIds.map((id) => (
              <Thumb key={id} id={id} onRemove={() => field.remove(id)} />
            ))}
          </div>
        )
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex aspect-video w-full max-w-xs flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-6 text-gray-500 transition-colors hover:border-gray-400 dark:border-gray-600"
        >
          <ImageIcon className="mb-2 h-8 w-8" />
          <span className="text-sm">{t("shared.mediaPicker.selectOrUpload")}</span>
        </button>
      )}

      {!isMultiple && selectedIds.length > 0 && (
        <button type="button" onClick={() => setOpen(true)} className="btn-ghost mt-2 text-xs">
          <FolderOpen className="mr-1 inline h-3.5 w-3.5" />
          {t("shared.mediaPicker.change")}
        </button>
      )}

      {open && modal}
    </div>
  );
}

interface ModalProps {
  folder: MediaFolder;
  /** Hodnota pole editoru (jednoduchý výběr: nejvýš jedno ID). */
  selectedIds: string[];
  mode: "single" | "multiple" | "gallery";
  /** Jednoduchý výběr: vybraná fotka (pole ji převezme a okno zavře). */
  onSelect: (id: string) => void;
  /** Vícenásobný výběr: „Přidat vybrané (N)“, ID v pořadí výběru; jednou za zavření okna. */
  onAdd: (ids: string[]) => void;
  onClose: () => void;
  /** Okno otevřené z hero místa (`CommonProps.purpose`); nepovinné. */
  purpose?: "hero";
}

/** Seznam okna se při každém otevření obnoví (keš 5 min by skryla fotky nahrané mezitím jinudy). */
const PICKER_LIBRARY_OPTIONS = { refetchOnMount: "always" } as const;

/** Typy pole, nad kterými Enter tlačítko stiskne, ne odešle formulář. */
const BUTTON_INPUT_TYPES: ReadonlySet<string> = new Set(["button", "submit", "reset", "image"]);

/**
 * Odešle by Enter nad `target` implicitně formulář editoru? `true` jen pro `Enter` nad polem
 * `INPUT` (ne `textarea`, tlačítka, pole typu tlačítka ani odkazy). Okno se vykresluje uvnitř
 * `<form>` editoru, proto panel okna nad takovým Enterem volá `preventDefault`.
 */
export function isImplicitSubmitKey(key: string, target: EventTarget | null): boolean {
  if (key !== "Enter" || target === null) return false;
  const el = target as { tagName?: unknown; type?: unknown };
  if (el.tagName !== "INPUT") return false;
  return !BUTTON_INPUT_TYPES.has(typeof el.type === "string" ? el.type.toLowerCase() : "text");
}

/**
 * Filtry okna v místním stavu (precedent `MediaPage`, ale bez adresy: adresa patří editoru
 * a zavřením okna filtry zanikají). Počáteční parametry nese model (`pickerInitialParams`:
 * země editoru, druh ne); země z rozsahu, která dorazí až po otevření (seznam destinací
 * za studena), se doplní úpravou v renderu, dokud redaktor zemi nezměnil ani filtry nezrušil.
 * `setFilter` a `clearFilters` jsou stabilní: `MediaFilters` posílá hledání po pauze efektem
 * nad `onChange` a nová funkce v každém renderu by časovač nulovala.
 */
function usePickerFilters(): {
  filters: MediaLibraryFilters;
  setFilter: (key: MediaFilterKey, value: string | null) => void;
  clearFilters: () => void;
} {
  const scope = useMediaPickerScope();
  const [params, setParams] = useState(() => pickerInitialParams(scope.countryId));
  const [countryTouched, setCountryTouched] = useState(false);
  // `pickerParamsForScope` vrací vždy novou kopii: porovnává se jen hodnota země, jinak by se
  // úprava v renderu zacyklila.
  const synced = pickerParamsForScope(params, scope.countryId, countryTouched);
  if (synced.get("country") !== params.get("country")) setParams(synced);

  const filters = useMemo(() => pickerFilters(params), [params]);
  const setFilter = useCallback((key: MediaFilterKey, value: string | null) => {
    setParams((p) => setMediaFilter(p, key, value));
    if (key === "country") setCountryTouched(true);
  }, []);
  const clearFilters = useCallback(() => {
    setParams(clearMediaFilters());
    setCountryTouched(true);
  }, []);
  return { filters, setFilter, clearFilters };
}

/**
 * Index fotky v prohlížeči okna. Prohlížeč patří k jednomu seznamu: po změně filtrů se zavře
 * a index se ořízne do rozsahu načtených fotek (obojí v renderu, precedent `MediaPage`).
 */
function usePickerViewer(
  filters: MediaLibraryFilters,
  itemsLength: number,
): { index: number | null; setIndex: (index: number | null) => void } {
  const [index, setIndex] = useState<number | null>(null);
  const filtersKey = JSON.stringify(filters);
  const [viewerFiltersKey, setViewerFiltersKey] = useState(filtersKey);
  if (viewerFiltersKey !== filtersKey) {
    setViewerFiltersKey(filtersKey);
    setIndex(null);
  }
  if (index !== null && index >= itemsLength) setIndex(itemsLength > 0 ? itemsLength - 1 : null);
  return { index, setIndex };
}

/**
 * Okno výběru fotek (vize podnety-tymu-2026-10, C7, F7.11): Knihovna (celá knihovna s filtry
 * stránky Média bez koše, země a druh předvyplněné, ★ první, celkový počet, donačítání,
 * prohlížeč s „Vybrat“) a Nahrát (panel dávky, F7.8). Filtry, výběr, prohlížeč, dávka
 * a hlídání odchodu žijí v okně, ne v záložce: přepnutí záložky je nezruší a panel dávky čte
 * zemi z filtru. Zavření okno odmontuje, takže zahodí výběr i filtry.
 *
 * Jednoduchý výběr: klik na fotku vybere a zavře. Vícenásobný: klik přepne výběr okna,
 * pole editoru mění jen „Přidat vybrané (N)“ (`onAdd`). Křížek, Esc, klik mimo, „Zrušit“,
 * „Přidat vybrané“ i výběr v jednoduchém výběru jdou přes jednu stráž zavření: během dávky
 * se zeptá dialog odchodu.
 *
 * Co z dávky vybrat, odvozuje okno ze stavu souborů (`batchUploadHost.ts`): jednoduchý výběr
 * se po konci dávky zavře s první vybratelnou fotkou, vícenásobný dává nahrané a existující
 * fotky průběžně do výběru okna.
 */
/**
 * Záložka Nahrát okna: panel dávky v režimu okna se zemí z filtru okna (precedent
 * `MediaUploadArea`). Panel čte zemi jen při připojení, proto se připojí až nad načteným (nebo
 * chybovým) seznamem destinací: zemi rozsahu editoru zná okno až s tímtéž seznamem
 * (`useMediaPickerScope`, týž klíč dotazu), takže dřív by filtr země ještě nebyl vyplněný a
 * panel by zemi nepřevzal.
 */
export function UploadTab({
  batch,
  folder,
  country,
  selectedIds,
  onDone,
}: {
  batch: BatchUpload;
  folder: MediaFolder;
  /** `filters.country` okna (ID země, `"none"`, `"any"` nebo nic). */
  country: string | undefined;
  selectedIds: string[];
  onDone: () => void;
}) {
  const destinations = useAdminDestinationList();
  const rows = destinations.data?.data ?? (destinations.isError ? NO_ROWS : undefined);
  const initialCountryIds = useMemo(
    () => (rows === undefined ? null : uploadCountriesFromFilter(country, rows)),
    [country, rows],
  );
  if (initialCountryIds === null) {
    return (
      <div className="flex h-40 items-center justify-center text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  return (
    <BatchUploadPanel
      batch={batch}
      mode="picker"
      folder={folder}
      initialCountryIds={initialCountryIds}
      selectedIds={selectedIds}
      onDone={onDone}
    />
  );
}

export function MediaModal({
  folder,
  selectedIds,
  mode,
  onSelect,
  onAdd,
  onClose,
  purpose,
}: ModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isMultiple = mode !== "single";
  const [tab, setTab] = useState<"library" | "upload">("library");
  // Po dávce seznam okna i stránky Média (oba pod `MEDIA_LIST_KEY`) ukážou nové fotky.
  const invalidateMediaLists = () => {
    void queryClient.invalidateQueries({ queryKey: MEDIA_LIST_KEY });
  };
  // Zavření během běhu (i „Odejít“ z odkazu nebo Zpět) zneplatní seznamy při odpojení okna
  // (`useHostedBatch`): letící požadavky na serveru doběhnou.
  const { batch, guard } = useHostedBatch(invalidateMediaLists);

  // Mimo běh dávky `confirmLeave` odpoví `true` hned (hlídání neběží), během běhu se zeptá.
  const requestClose = (after: () => void = onClose) => {
    void guard.confirmLeave().then((leave) => {
      if (leave) after();
    });
  };

  // Klik mimo zavře jen při stisku i puštění na pozadí: tažení výběrem textu z okna ven
  // okno nezavře a klik na pozadí dialogu odchodu (probublá stromem komponent) také ne.
  const pressedOnBackdrop = useRef(false);

  // Filtry a seznam okna (stav okna, ne adresy: adresa patří editoru, zavření filtry zahodí).
  const { filters, setFilter, clearFilters } = usePickerFilters();
  const library = useMediaLibrary(filters, PICKER_LIBRARY_OPTIONS);
  const sources = useMediaSources();
  const grid = mediaGridState({
    isLoading: library.isLoading,
    isError: library.isError,
    isFetchNextPageError: library.isFetchNextPageError,
    itemsLength: library.items.length,
  });
  const activeCount = activeMediaFilterCount(filters);

  // Výběr okna (pořadí výběru = pořadí přidání); zavření okna ho zahodí (odmontování).
  const [picked, setPicked] = useState<string[]>([]);
  const ctx: PickerContext = {
    mode: isMultiple ? "multiple" : "single",
    value: selectedIds,
    picked,
  };
  const stateOf = (id: string) => pickerTileState(id, ctx);
  // Jednoduchý výběr vybere a zavře přes stráž zavření (během dávky se zeptá); u „Vybraná“
  // totéž. Vícenásobný přepne výběr okna („V galerii“ model nechá beze změny).
  const onActivate = (id: string) => {
    if (!isMultiple) {
      requestClose(() => onSelect(id));
      return;
    }
    setPicked((p) => togglePicked({ ...ctx, picked: p }, id));
  };

  const viewer = usePickerViewer(filters, library.items.length);
  const onOpenViewer = (id: string) => {
    const index = library.items.findIndex((m) => m.id === id);
    if (index !== -1) viewer.setIndex(index);
  };

  // Esc: posluchač se registruje jednou, `requestClose` z aktuálního renderu (běh dávky,
  // `confirmLeave`), otevřený dialog odchodu a otevřený prohlížeč (vlastní stav okna) čte přes
  // ref. Esc, který si vzal vnořený prvek (nabídka zemí, dialog odchodu volají
  // `preventDefault`), Esc nad čekajícím dialogem odchodu a Esc s otevřeným prohlížečem (zavře
  // jen prohlížeč, výběr zůstane) okno nezavírají. Posluchač okna se registruje dřív než
  // posluchač prohlížeče, takže v tu chvíli je prohlížeč ve stavu okna ještě otevřený.
  const requestCloseRef = useRef(requestClose);
  const closePendingRef = useRef(false);
  const viewerOpenRef = useRef(false);
  // Výběr okna v okamžiku odpovědi dialogu odchodu: dávka může během dialogu do výběru přidat
  // další fotky a „Přidat vybrané“ má přidat to, co patička ukazuje, ne stav z kliku.
  const pickedRef = useRef(picked);
  // `useLeaveGuard` vrací dialog jen nad čekajícím `confirmLeave()`, jinak `null`.
  const closePending = guard.dialog !== null;
  useLayoutEffect(() => {
    requestCloseRef.current = requestClose;
    closePendingRef.current = closePending;
    viewerOpenRef.current = viewer.index !== null;
    pickedRef.current = picked;
  });
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const state = { closePending: closePendingRef.current, viewerOpen: viewerOpenRef.current };
      if (!shouldCloseOnEscape(e, state)) return;
      requestCloseRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Jednoduchý výběr: po konci dávky zavře okno s nahranou nebo existující fotkou
  // (`MediaPicker.handleSelect`; dávka už neběží, stráž se neptá).
  const choice = isMultiple ? null : singlePickerChoice(batch);
  const chosen = useRef<string | null>(null);
  useEffect(() => {
    if (choice === null || choice === chosen.current) return;
    chosen.current = choice;
    onSelect(choice);
  }, [choice, onSelect]);

  // Výběr více fotek: každý vybratelný soubor dávky jde jednou do výběru okna; fotku, která už
  // je v poli editoru („V galerii“) nebo ve výběru, vyřadí `addPicked`. Pole editoru se nemění,
  // dokud redaktor nedá „Přidat vybrané“.
  const handled = useRef<Set<string> | null>(null);
  const { files, startedAt } = batch;
  useEffect(() => {
    handled.current ??= new Set();
    if (startedAt === null) {
      handled.current.clear();
      return;
    }
    if (!isMultiple) return;
    const { ids, keys } = pickerAdditions({ files, startedAt }, handled.current);
    for (const k of keys) handled.current.add(k);
    if (ids.length > 0) {
      setPicked((p) => addPicked({ mode: "multiple", value: selectedIds, picked: p }, ids));
    }
  }, [isMultiple, files, startedAt, selectedIds]);

  const backToLibrary = () => {
    batch.reset();
    setTab("library");
  };
  const tabClass = (active: boolean) =>
    `rounded-md px-3 py-1.5 text-sm font-medium ${active ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300" : "text-gray-500"}`;
  const viewerItem = viewer.index !== null ? library.items[viewer.index] : undefined;

  return (
    <div
      data-media-picker=""
      data-mode={mode}
      data-purpose={purpose ?? "none"}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        pressedOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        const onBackdrop = pressedOnBackdrop.current && e.target === e.currentTarget;
        pressedOnBackdrop.current = false;
        if (onBackdrop) requestClose();
      }}
    >
      <div
        className="flex h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-900"
        // Okno leží uvnitř `<form>` editoru: Enter v poli hledání by formulář odeslal.
        onKeyDown={(e) => {
          if (isImplicitSubmitKey(e.key, e.target)) e.preventDefault();
        }}
      >
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <div className="flex flex-wrap items-center gap-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {isMultiple
                ? t("mediaPicker.window.titleMultiple")
                : t("mediaPicker.window.titleSingle")}
            </h2>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setTab("library")}
                className={tabClass(tab === "library")}
              >
                {t("mediaPicker.tabs.library")}
              </button>
              <button
                type="button"
                onClick={() => setTab("upload")}
                className={tabClass(tab === "upload")}
              >
                {t("common.actions.upload")}
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => requestClose()}
            aria-label={t("common.actions.close")}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div data-media-picker-scroll="" className="flex-1 overflow-y-auto p-4">
          {tab === "library" ? (
            <LibraryTab
              filters={filters}
              onFilter={setFilter}
              onClearFilters={clearFilters}
              activeCount={activeCount}
              sources={sources.data ?? []}
              grid={grid}
              library={library}
              stateOf={stateOf}
              onActivate={onActivate}
              onOpenViewer={onOpenViewer}
              onGoUpload={() => setTab("upload")}
              purpose={purpose}
            />
          ) : (
            <UploadTab
              batch={batch}
              folder={folder}
              country={filters.country}
              selectedIds={selectedIds}
              onDone={backToLibrary}
            />
          )}
        </div>
        {isMultiple && tab === "library" && (
          <PickerFooter
            count={picked.length}
            onCancel={() => requestClose()}
            onAdd={() =>
              requestClose(() => {
                onAdd(pickedRef.current);
                onClose();
              })
            }
          />
        )}
      </div>
      {guard.dialog}
      {/* Prohlížeč je poslední potomek kořene: při shodném z-50 leží nad oknem. */}
      {viewer.index !== null && viewerItem && (
        <PhotoViewer
          mode="picker"
          items={library.items}
          index={viewer.index}
          total={library.total ?? library.items.length}
          hasMore={library.hasNextPage}
          onIndexChange={viewer.setIndex}
          // Chyba donačtení slib odmítne, prohlížeč ji ukáže a index nechá; obnovu po
          // zneplatnění donačtení neruší.
          onLoadMore={async () => {
            await library.fetchNextPage({ throwOnError: true, cancelRefetch: false });
          }}
          onClose={() => viewer.setIndex(null)}
          onChanged={library.patchItem}
          pick={{ stateOf, onPick: onActivate }}
        />
      )}
    </div>
  );
}

function LibraryTab({
  filters,
  onFilter,
  onClearFilters,
  activeCount,
  sources,
  grid,
  library,
  stateOf,
  onActivate,
  onOpenViewer,
  onGoUpload,
  purpose,
}: {
  filters: MediaLibraryFilters;
  onFilter: (key: MediaFilterKey, value: string | null) => void;
  onClearFilters: () => void;
  activeCount: number;
  sources: readonly string[];
  grid: ReturnType<typeof mediaGridState>;
  library: MediaLibrary;
  stateOf: (id: string) => PickerTileState;
  onActivate: (id: string) => void;
  onOpenViewer: (id: string) => void;
  onGoUpload: () => void;
  purpose?: "hero";
}) {
  const { t } = useTranslation();
  // `cancelRefetch: false`: donačtení nezruší obnovu po zneplatnění; stráž proti donačtení
  // během obnovy drží kontejner mřížky nad `fetching`. Stabilní: pozorovatel mřížky se
  // přihlašuje znovu při změně `onLoadMore` (`fetchNextPage` dotazu je vázaná metoda).
  const { fetchNextPage } = library;
  const loadMore = useCallback(() => void fetchNextPage({ cancelRefetch: false }), [fetchNextPage]);
  return (
    <div className="flex gap-4">
      {/* Levý sloupec: alba (bez správy, její dialog je `<form>`) a druh */}
      <aside className="hidden w-48 shrink-0 space-y-3 sm:block">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          {t("media.albums.title")}
        </h3>
        <AlbumTree
          selectedId={filters.folderId ?? null}
          onSelect={(id) => onFilter("folderId", id)}
        />
        <MediaFolderFilter value={filters.folder} onChange={(f) => onFilter("folder", f)} />
      </aside>
      <div className="min-w-0 flex-1 space-y-4">
        <MediaFilters
          filters={filters}
          onChange={onFilter}
          onClear={onClearFilters}
          activeCount={activeCount}
          showClear={activeCount > 0 && grid.state !== "empty"}
          sources={sources}
          hideTrash
        />
        <PickerGrid
          state={grid.state}
          items={library.items}
          total={library.total}
          hasMore={library.hasNextPage}
          loadingMore={library.isFetchingNextPage}
          fetching={library.isFetching}
          loadMoreError={grid.nextPageError}
          activeFilterCount={activeCount}
          stateOf={stateOf}
          onActivate={onActivate}
          onOpenViewer={onOpenViewer}
          // Po chybě další stránky znovu tu stránku, jinak celý seznam (precedent `MediaGrid`).
          onRetry={() => {
            if (grid.nextPageError) void library.fetchNextPage({ cancelRefetch: false });
            else void library.refetch();
          }}
          onLoadMore={loadMore}
          onClearFilters={onClearFilters}
          onGoUpload={onGoUpload}
          purpose={purpose}
        />
      </div>
    </div>
  );
}
